import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SERVICES, PRICING } from "@/lib/content";

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Services"
        title="Solutions for modern workforces."
        subtitle="From boardroom-level HR strategy to the operational systems that run payroll every other Friday — Groman Associates covers the full lifecycle of workforce leadership."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Services" }]}
        image="https://cdn.prod.website-files.com/6a46f71ec93c5cc481e87205/6a46fcc5c5f9beadb1863980_rodeo-project-management-software-ONe-snuCaqQ-unsplash.jpg"
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {SERVICES.map((svc, idx) => (
          <Reveal key={svc.slug}>
            <section
              id={svc.slug}
              data-testid={`services-section-${svc.slug}`}
              className={`grid lg:grid-cols-12 gap-12 py-20 ${idx !== SERVICES.length - 1 ? "border-b border-gray-200" : ""}`}
            >
              <div className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="aspect-[4/5] overflow-hidden bg-offwhite group">
                  <img
                    src={svc.image}
                    alt={svc.label}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
              <div className={`lg:col-span-7 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="font-mono text-xs text-gold tracking-[0.24em] uppercase">0{idx + 1} · {svc.tag}</div>
                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold tracking-tight text-charcoal leading-tight">{svc.label}</h2>
                <p className="mt-5 text-charcoal-soft text-base md:text-lg leading-relaxed max-w-xl">{svc.summary}</p>

                <div className="mt-10 grid sm:grid-cols-2 gap-0 grid-borders">
                  {svc.items.map((it) => (
                    <div key={it.title} className="p-6 bg-white">
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 border border-gold grid place-items-center shrink-0 mt-0.5">
                          <Check className="w-3.5 h-3.5 text-gold" />
                        </div>
                        <div>
                          <div className="font-heading font-medium text-charcoal">{it.title}</div>
                          <p className="mt-2 text-sm text-charcoal-soft leading-relaxed">{it.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Link to="/contact" data-testid={`svc-cta-${svc.slug}`} className="mt-10 inline-flex items-center gap-2 text-charcoal font-medium gold-link">
                  Discuss this engagement <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </section>
          </Reveal>
        ))}
      </div>

      {/* PRICING */}
      <section className="bg-offwhite border-y border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <Reveal>
            <div className="grid lg:grid-cols-12 gap-12 mb-14">
              <div className="lg:col-span-6">
                <div className="overline mb-4">Investment Structure</div>
                <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-charcoal leading-tight">Transparent pricing.</h2>
              </div>
              <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                <p className="text-charcoal-soft leading-relaxed">Structured for HR consulting projects, retainer partnerships, and full executive engagements. Every engagement is scoped in writing before work begins.</p>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRICING.map((p, i) => (
              <Reveal key={p.tier} delay={i * 0.07}>
                <div
                  data-testid={`pricing-tier-${i}`}
                  className={`p-8 border h-full flex flex-col ${
                    p.highlight
                      ? "bg-charcoal text-white border-charcoal relative"
                      : "bg-white border-gray-200"
                  }`}
                >
                  {p.highlight && (
                    <div className="absolute top-0 left-0 bg-gold text-white font-mono text-[10px] uppercase tracking-[0.24em] px-3 py-1">Most Popular</div>
                  )}
                  <div className={`font-mono text-xs uppercase tracking-[0.24em] ${p.highlight ? "text-gold" : "text-gold"} mb-6 ${p.highlight ? "mt-6" : ""}`}>{p.tier}</div>
                  <div className="flex items-baseline gap-2">
                    <span className={`font-heading text-4xl md:text-5xl font-semibold ${p.highlight ? "text-white" : "text-charcoal"}`}>{p.price}</span>
                  </div>
                  <div className={`mt-2 text-sm ${p.highlight ? "text-white/60" : "text-charcoal-soft"}`}>{p.unit}</div>

                  <div className={`my-8 h-px ${p.highlight ? "bg-white/10" : "bg-gray-200"}`} />

                  <ul className="space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex gap-3 items-start">
                        <Check className={`w-4 h-4 shrink-0 mt-0.5 text-gold`} />
                        <span className={`text-sm ${p.highlight ? "text-white/85" : "text-charcoal"}`}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    data-testid={`pricing-cta-${i}`}
                    className={`mt-10 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
                      p.highlight
                        ? "bg-gold text-white hover:bg-gold-hover"
                        : "border border-charcoal text-charcoal hover:bg-charcoal hover:text-white"
                    }`}
                  >
                    Inquire now <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <div className="overline mb-3">Find the right solution now</div>
            <h3 className="font-heading text-2xl md:text-4xl text-charcoal font-semibold leading-tight max-w-2xl">We build practical strategies for your toughest people problems. Let us get to work.</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="services-request-cta" className="inline-flex items-center gap-2 bg-gold text-white px-6 py-4 text-sm font-medium hover:bg-gold-hover transition-colors">
              Request a consultation <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/government-contracting" data-testid="services-govcon-cta" className="inline-flex items-center gap-2 border border-charcoal text-charcoal px-6 py-4 text-sm hover:bg-charcoal hover:text-white transition-colors">
              Explore GovCon
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
